import React, { createRef } from 'react';
import {
  AppBar,
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Drawer,
} from '@mui/material';
import { Outlet } from 'react-router-dom';
import { ReadingProgress } from '../../shared/ui/ReadingProgress';
import Navigation from './Navigation';
import ToolBar from './ToolBar';
import { useAuth } from '../providers/AuthProvider';
import { useThemeMode } from '../providers/ThemeModeProvider';
import { useUI } from '../providers/UIProvider';
import { tokens } from '../../shared/theme/tokens';

const DRAWER_WIDTH = tokens.layout.drawerWidth;

export function AppLayout() {
  const { darkState, handleThemeChange } = useThemeMode();
  const { user, logout, isLoading } = useAuth();
  const { phoneResponsive, openBackdrop, setZoom } = useUI();
  const target = createRef<HTMLElement>();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);
  const handleDrawerClose = () => setMobileOpen(false);

  if (isLoading && !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress color={darkState ? 'secondary' : 'primary'} />
      </Box>
    );
  }

  if (!user) {
    return null;
  }

  const handleRestoreUser = () => {
    localStorage.setItem('REACT_TOKEN_AUTH', localStorage.getItem('REACT_TOKEN_AUTH_ORIGIN') ?? '');
    localStorage.setItem('REACT_ROLE', localStorage.getItem('REACT_ROLE_ORIGIN') ?? '');
    localStorage.removeItem('REACT_TOKEN_AUTH_ORIGIN');
    localStorage.removeItem('REACT_ROLE_ORIGIN');
    setTimeout(() => {
      window.location.reload();
    }, 200);
  };

  const drawerPaperSx = {
    boxSizing: 'border-box',
    width: DRAWER_WIDTH,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        color="inherit"
        sx={{
          width: { xs: 'calc(100% - 16px)', sm: `calc(100% - ${DRAWER_WIDTH}px - 16px)` },
          ml: { xs: 1, sm: `${DRAWER_WIDTH}px` },
        }}
      >
        <ToolBar
          darkState={darkState}
          handleThemeChange={handleThemeChange}
          handleDisconnect={logout}
          user={user}
          phoneResponsive={phoneResponsive}
          handleRestoreUser={handleRestoreUser}
          onMenuClick={handleDrawerToggle}
        />
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: DRAWER_WIDTH }, flexShrink: { sm: 0 } }}
        aria-label="Navigation principale"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
        >
          <Navigation
            darkState={darkState}
            setZoom={setZoom}
            phoneResponsive={phoneResponsive}
            setOpen={handleDrawerClose}
          />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': drawerPaperSx,
          }}
          open
        >
          <Navigation
            darkState={darkState}
            setZoom={setZoom}
            phoneResponsive={phoneResponsive}
            setOpen={handleDrawerClose}
          />
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          px: tokens.layout.contentPaddingX,
          pb: 3,
        }}
        ref={target}
      >
        <ReadingProgress target={target} darkState={darkState} />
        <Backdrop sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, color: '#fff' }} open={openBackdrop}>
          <CircularProgress color="inherit" />
        </Backdrop>
        <Box sx={{ height: { xs: 72, sm: 80 } }} />
        <Outlet />
      </Box>
    </Box>
  );
}
